import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import type { Connection, ConnectionSummary } from '@/shared/api/generated/catalog';
import { ageText } from '@/shared/model/age';

import { harvestActivity, scoredCoverage } from '../model/catalogue';

/* One source, as the way into its catalogue — the design's connection card.
 *
 * **Deliberately not `SourceCard`.** That card is about managing the *connection* — verify it,
 * harvest it, retire it — and it lives on the Connections screen where those are the actions. This
 * one is the way into the *catalogue behind* the connection: how much is in it, how much has been
 * scored, how recently it was read. The whole card is one link with no buttons, so a reader can click
 * anywhere without checking what is under the cursor first.
 *
 * **The pill is the connection's state**, as the design draws it: whether the source can be reached
 * is what decides whether what is behind it can be trusted to be current.
 *
 * **Every figure says whether it was measured.** A source with nothing harvested shows a dash, not a
 * nought; `scored` may be null, and a running harvest's progress is a different fact from a finished
 * one's age. The coverage bar fills only from a measured share — the hatched track that shows
 * through otherwise is the design's mark for "not measured".
 */

/* A total map with `satisfies`, so a fifth connection state is a compile error here rather than a
 * pill silently rendering in the everything-is-fine tone. */
const STATE_TONE = {
  verified: 'ok',
  failed: 'error',
  registered: 'unknown',
  disabled: 'unknown',
} satisfies Record<Connection['state'], 'ok' | 'error' | 'unknown'>;

/* "Never checked" wears the hollow dot: nothing has been measured about the source yet. Out of
 * service is a decision somebody made, so its dot is solid. */
const HOLLOW = {
  verified: false,
  failed: false,
  registered: true,
  disabled: false,
} satisfies Record<Connection['state'], boolean>;

export interface SourceCatalogueCardProps {
  readonly summary: ConnectionSummary;
  /** The connection itself, when the connections read has resolved it. `undefined` means the source
   * is in the summary and not in the connection listing — see the card body for what it renders. */
  readonly connection: Connection | undefined;
  /** `true` while the connection listing is loading or failed, which is why a name may be absent. */
  readonly namesUnread: boolean;
  /** How the connector family is spelled, and the address to show on hover. */
  readonly connectorLabel: (connection: Connection) => string;
  readonly address: (connection: Connection) => string;
  readonly to: string;
}

export function SourceCatalogueCard({
  summary,
  connection,
  namesUnread,
  connectorLabel,
  address,
  to,
}: SourceCatalogueCardProps): ReactElement {
  const { t } = useTranslation();
  const activity = harvestActivity(summary);
  const coverage = scoredCoverage(summary);
  const ago =
    summary.last_harvest_at === null
      ? null
      : ageText(t, summary.last_harvest_at, new Date());

  /* The bar's fill, from a measured coverage only. A zero-width fill on an unconsulted source would
   * read as "measured, and nothing is scored" — the claim the hatched track exists to refuse. */
  const filled =
    coverage.kind === 'measured' && coverage.total > 0
      ? Math.round((coverage.covered / coverage.total) * 100)
      : 0;

  return (
    <article className="lq-dsc">
      <Link className="lq-dsc__link" to={to}>
        <span className="lq-dsc__head">
          {/* A source with no name is a real state and gets a sentence rather than its id: the
              summary read and the connection listing are two requests, so one can arrive first,
              fail, or be capped while the other succeeded. */}
          <h3 className="lq-dsc__name">
            {connection !== undefined ? (
              connection.name
            ) : (
              <span className="lq-dim">
                {namesUnread
                  ? t('catalogue.source.nameUnread')
                  : t('catalogue.source.unlisted')}
              </span>
            )}
          </h3>
          {connection !== undefined ? (
            <span
              className="lq-cn__state lq-dsc__state"
              data-tone={STATE_TONE[connection.state]}
              title={t(`connections.state.${connection.state}.meaning`)}
            >
              <span
                aria-hidden="true"
                className={
                  HOLLOW[connection.state]
                    ? 'lq-cn__dot lq-cn__dot--hollow'
                    : 'lq-cn__dot'
                }
              />
              {t(`catalogue.card.conn.${connection.state}`)}
            </span>
          ) : null}
        </span>

        {connection !== undefined ? (
          <span className="lq-dsc__type" title={address(connection)}>
            {connectorLabel(connection)}
          </span>
        ) : null}

        <span className="lq-dsc__count">
          <span className="lq-dsc__countValue">
            {summary.datasets === 0 ? '—' : summary.datasets.toLocaleString()}
          </span>
          <span className="lq-dsc__countLabel">
            {t('catalogue.card.datasets', { count: summary.datasets })}
          </span>
        </span>

        {summary.datasets > 0 ? (
          <span className="lq-dsc__bar" aria-hidden="true">
            <span className="lq-dsc__barFill" style={{ width: `${String(filled)}%` }} />
          </span>
        ) : null}

        <dl className="lq-dsc__foot">
          <div>
            <dt className="lq-visually-hidden">{t('catalogue.stat.scored')}</dt>
            <dd>
              {/* A count for the one case that genuinely means it; words, with the reason on hover,
                  for the two that are not a measurement. A `0 scored` on an unconsulted source
                  would claim scoring ran and found nothing. */}
              {summary.datasets === 0 ? (
                t('catalogue.card.nothingHarvested')
              ) : coverage.kind === 'measured' ? (
                t('catalogue.source.scoredCount', { count: coverage.covered })
              ) : (
                <span title={t(`catalogue.scored.${coverage.kind}`)}>
                  {t(`catalogue.card.scored.${coverage.kind}`)}
                </span>
              )}
            </dd>
          </div>
          <div className="lq-dsc__footEnd">
            <dt className="lq-visually-hidden">{t('catalogue.stat.lastHarvest')}</dt>
            <dd>
              {/* A running harvest reports progress instead of an age, because the age belongs to
                  the *previous* run and putting it here while a new one is in flight reads as the
                  age of what is on screen. */}
              {activity.kind === 'running'
                ? t('catalogue.harvest.progress', {
                    observed: activity.observed,
                    discovered: activity.discovered,
                  })
                : activity.kind === 'starting'
                  ? t('catalogue.harvest.starting')
                  : summary.last_harvest_at === null
                    ? t('catalogue.card.neverHarvested')
                    : /* An instant the age helper cannot read falls back to the raw instant rather
                         than to "Never harvested", which would be a claim about the source. */
                      t('catalogue.card.harvested', {
                        ago: ago ?? summary.last_harvest_at,
                      })}
            </dd>
          </div>
        </dl>
      </Link>
    </article>
  );
}
